// ---------------------------------------------------------------------------
// themeConfigService — leitura das sobreposições de tema gravadas pelo painel,
// e a escrita (admin) via callable.
//
// Blueprint: `docs/systems/admin-themes.md`.
//
// Coleção `theme_configs/{themeId}`: `read: if isAuthed()`, `write: false`.
// A escrita passa por `adminSetThemeConfig`, que tem `assertAdmin` e a banda de
// plausibilidade do preço — o client nunca grava preço, porque preço debita
// carteira.
// ---------------------------------------------------------------------------

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase/config";
import type { ThemeConfig, ThemeConfigMap } from "../../config/themeConfigs";

const COLLECTION = "theme_configs";

/**
 * Lê todas as sobreposições.
 *
 * ⚠️ **Falha devolve mapa VAZIO, nunca lança.** Sem sobreposição o app mostra o
 * catálogo do código, que é a resposta correta e completa — a Loja continua
 * vendendo e ninguém vê erro. Propagar a exceção transformaria uma camada
 * opcional num ponto único de falha da vitrine inteira, que é exatamente o
 * risco que a decisão de "os 26 ficam no código" existe para evitar.
 *
 * São no máximo ~26 docs minúsculos, então é uma leitura de coleção inteira
 * sem paginação de propósito: paginar aqui custaria mais código que dado.
 */
export async function fetchThemeConfigs(): Promise<ThemeConfigMap> {
  try {
    const snap = await getDocs(collection(db, COLLECTION));
    const out: Record<string, ThemeConfig> = {};
    for (const d of snap.docs) out[d.id] = d.data() as ThemeConfig;
    return out;
  } catch {
    return {};
  }
}

export interface SetThemeConfigInput {
  themeId: string;
  /** Ausente = não mexi. Número = novo preço. `null` = volta ao do código. */
  price?: number | null;
  /** Ausente = não mexi. Id = esta temporada. `null` = SEM temporada. */
  seasonId?: string | null;
  storeEnabled?: boolean | null;
  showcasePinned?: boolean | null;
  /** Janela do destaque em ms. `null` remove aquele limite. */
  showcaseFrom?: number | null;
  showcaseUntil?: number | null;
  /**
   * Janela LIVRE da temporada, chave de dia BR ("AAAA-MM-DD"), as duas pontas
   * inclusivas. `null` remove a ponta e devolve o tema à temporada nomeada.
   */
  seasonFrom?: string | null;
  seasonUntil?: string | null;
  /** Vai para a trilha de auditoria. Preço é dinheiro. */
  reason: string;
}

export interface SetThemeConfigResult {
  themeId: string;
  config: ThemeConfig;
  /** Vazio = a chamada não mudou nada (idempotente, não é erro). */
  changed: string[];
}

export interface SaveThemeInput {
  themeId: string;
  label: string;
  description: string;
  price: number;
  tokens: Record<string, string>;
  exportPalette: Record<string, string>;
  /** `false` grava rascunho; o portão de contraste só barra a publicação. */
  publish: boolean;
  reason: string;
}

export interface SaveThemeResult {
  themeId: string;
  status: "draft" | "published";
  contrast: {
    floor: number;
    passes: boolean;
    failures: { id: string; ratio: number; target: number }[];
  };
}

/**
 * Cria ou atualiza um tema desenhado no painel.
 *
 * ⚠️ O portão de contraste roda no SERVIDOR também. Esta chamada pode voltar
 * `failed-precondition` mesmo com a tela mostrando aprovado — e se isso
 * acontecer é sinal de que as duas medições divergiram, o que o teste de
 * paridade existe para impedir.
 */
export async function saveTheme(input: SaveThemeInput): Promise<SaveThemeResult> {
  const fn = httpsCallable<SaveThemeInput, SaveThemeResult>(
    getFunctions(getApp()),
    "adminSaveTheme",
  );
  const res = await fn(input);
  return res.data;
}

/** Grava a sobreposição. Só admin — a callable recusa o resto. */
export async function setThemeConfig(
  input: SetThemeConfigInput,
): Promise<SetThemeConfigResult> {
  const fn = httpsCallable<SetThemeConfigInput, SetThemeConfigResult>(
    getFunctions(getApp()),
    "adminSetThemeConfig",
  );
  const res = await fn(input);
  return res.data;
}
